import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/base/Button";
import Section from "components/base/Section";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import AssinaturaTable from "components/tables/AssinaturaTable";
import { assinaturaBreadcrumbItems, ModuloItemType } from "data/assinatura";
import { useState, useEffect } from "react";
import { Col, Row, Spinner, Form, Card } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fromDateInputValue } from "utils/dateUtils";

const fetchEmpresaModulos = async ({ queryKey }: any) => {
  const [, id] = queryKey;
  const { data } = await axios.get(`/api/admin/empresa/${id}/contrato`);
  return data;
};

export default function Assinatura() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: empresaModulos, isLoading: loadingEmpresaModulos } = useQuery({
    queryKey: ["empresaModulos", id],
    queryFn: fetchEmpresaModulos,
  });

  const [selectedProducts, setSelectedProducts] = useState<ModuloItemType[]>(
    []
  );
  const [dataBloqueio, setDataBloqueio] = useState<string>("");

  // Atualiza os módulos selecionados quando os dados da empresa são carregados
  useEffect(() => {
    if (empresaModulos) {
      const initialSelectedProducts = empresaModulos.flatMap((empresa: any) =>
        empresa.modulos.filter((modulo: any) => modulo.ativo)
      );
      setSelectedProducts(initialSelectedProducts);

      // Define a data de bloqueio se existir
      if (empresaModulos.length > 0 && empresaModulos[0].dataBloqueio) {
        const date = new Date(empresaModulos[0].dataBloqueio);
        setDataBloqueio(date.toISOString().split('T')[0]);
      }
    }
  }, [empresaModulos]);

  // Função para adicionar ou remover módulos da seleção
  const toggleProduct = (product: ModuloItemType) => {
    const isSelected = selectedProducts.some((p) => p.id === product.id);
    if (isSelected) {
      // Remove o módulo se já estiver selecionado
      setSelectedProducts(selectedProducts.filter((p) => p.id !== product.id));
    } else {
      // Adiciona o módulo se não estiver selecionado
      setSelectedProducts([...selectedProducts, product]);
    }
  };

  const mutation = useMutation({
    mutationFn: (data: any) => {
      return axios.post(`/api/admin/empresa/${id}/contrato`, data);
    },
  });

  const handleSalvar = async () => {
    await mutation.mutateAsync({
      modulos: selectedProducts.map((p) => p.id),
      dataBloqueio: dataBloqueio ? fromDateInputValue(dataBloqueio) : null,
    });

    navigate(`/revenda/clientes/${id}`);
  };

  if (loadingEmpresaModulos)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );

  return (
    <div className="pt-5 mb-9">
      <Section small className="py-0">
        <PageBreadcrumb items={assinaturaBreadcrumbItems(id)} />
        <h2 className="mb-6">Assinatura</h2>

        {/* Card para Data de Bloqueio */}
        <Card className="mb-4">
          <Card.Body>
            <h5 className="mb-3">Configurações de Bloqueio</h5>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Data de Bloqueio</Form.Label>
                  <Form.Control
                    type="date"
                    value={dataBloqueio}
                    onChange={(e) => setDataBloqueio(e.target.value)}
                    placeholder="Selecione uma data de bloqueio (opcional)"
                  />
                  <Form.Text className="text-muted">
                    Deixe vazio para não definir bloqueio
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Row className="g-5">
          <Col>
            {empresaModulos?.map((empresa: any) => {
              return (
                <div key={empresa.empresaId}>
                  <h5 className="mb-4">{empresa.razaoSocial}</h5>
                  <AssinaturaTable
                    products={empresa.modulos.map((modulo: any) => ({
                      ...modulo,
                      selected: modulo.ativo,
                    }))}
                    addProduct={(product: ModuloItemType) => {
                      toggleProduct(product);
                    }}
                    removeProduct={(product: ModuloItemType) => {
                      toggleProduct(product);
                    }}
                    selectedProducts={selectedProducts}
                  />
                </div>
              );
            })}
          </Col>
        </Row>
        <Col xs={12} className="mt-5 d-flex justify-content-end gap-3">
          <Button
            variant="phoenix-secondary"
            className="text-nowrap"
            type="button"
            as={Link}
            to={id ? `/revenda/clientes/${id}` : "/revenda/clientes"}
          >
            Sair sem salvar
          </Button>
          <Button
            variant="primary"
            className="px-8 px-sm-11 me-2"
            loadingPosition="end"
            onClick={handleSalvar}
            loading={mutation.isPending}
          >
            Salvar
          </Button>
        </Col>
      </Section>
    </div>
  );
}
